import type { MetricsRow } from 'app/services/metrics'
import { useSearchParams } from 'react-router-dom'
import { ColumnDef } from '@tanstack/react-table'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'
import { graphql } from 'app/generated/gql'
import { gqlQuery } from 'app/lib/graphqlClient'
import SimpleTable from 'app/components/table/SimpleTable'

const EMPTY_QUERY_PARAMS: string[] = []

const MONTHLY_METRICS_QUERY = graphql(`
  query MonthlyMarketingMetrics(
    $dateRange: DateRangeInput!
    $groupBy: String!
    $channel: String
    $locationIds: [String!]
    $showDisqualified: Boolean
  ) {
    marketingMetrics(
      dateRange: $dateRange
      groupBy: $groupBy
      channel: $channel
      locationIds: $locationIds
      showDisqualified: $showDisqualified
    ) {
      rows {
        bucket
        locationId
        leads
        MQLs
        mqls
        appointments
        patients
        budget
        production
        scheduledProduction
        ROI
        CPL
        CPQL
        CPA
        CAC
        cpls
        roi
        facebookSpend
        googleSpend
        targetSpend
        leadGoal
        iCPL
        iCPQL
      }
    }
  }
`)

const NEW_PATIENTS_MONTHLY_QUERY = graphql(`
  query NewPatientsMonthly($dateRange: DateRangeInput!, $locationIds: [String!], $channel: String) {
    newPatientsMonthlyBreakdown(dateRange: $dateRange, locationIds: $locationIds, channel: $channel) {
      bucket
      reappointmentPercentage
      brokenAppointmentCount
      averageTimeToAppointment
      averageProductionPerNewPatientVisit
      newPatients
      scheduledNewPatients
    }
  }
`)

export default function MonthlyLocationBreakdownTable({
  locationIds,
  columns: columnsProp,
  apiEndpoint,
  channel,
  queryParams = EMPTY_QUERY_PARAMS,
  getRowPatch,
}: {
  locationIds: string[]
  columns:
    | ColumnDef<MetricsRow>[]
    | ((onRowPatch: (row: MetricsRow, patch: Partial<MetricsRow>) => void) => ColumnDef<MetricsRow>[])
  apiEndpoint?: string
  channel?: string
  queryParams?: string[]
  getRowPatch?: (row: MetricsRow) => Partial<MetricsRow>
}) {
  const [searchParams] = useSearchParams()
  const [locationDetailRows, setLocationDetailRows] = useState<MetricsRow[]>()
  const [loading, setLoading] = useState(false)
  channel ??= searchParams.get(`channel`) || undefined
  const showDisqualified = searchParams.get(`showDisqualified`) === `true`
  const patchRow = useCallback((row: MetricsRow, patch: Partial<MetricsRow>) => {
    setLocationDetailRows((rows) => rows?.map((item) => (item === row ? { ...item, ...patch } : item)))
  }, [])
  const columns = typeof columnsProp === `function` ? columnsProp(patchRow) : columnsProp

  useEffect(() => {
    setLocationDetailRows(undefined)
    setLoading(true)

    const dateRange = { fromDate: `2000-01-01`, toDate: moment().add(1, `y`).endOf(`y`).format(`YYYY-MM-DD`) }

    if (apiEndpoint === `/api/metrics/new-patients`) {
      gqlQuery<{ newPatientsMonthlyBreakdown: MetricsRow[] }>(NEW_PATIENTS_MONTHLY_QUERY, {
        dateRange,
        locationIds,
        channel: channel || undefined,
      })
        .then((res) =>
          setLocationDetailRows(
            res.data?.newPatientsMonthlyBreakdown?.map((row) => ({ ...row, ...getRowPatch?.(row) })),
          ),
        )
        .finally(() => setLoading(false))
    } else {
      gqlQuery<{ marketingMetrics: { rows: MetricsRow[] } }>(MONTHLY_METRICS_QUERY, {
        dateRange,
        groupBy: `months`,
        channel: channel || undefined,
        locationIds,
        showDisqualified,
      })
        .then((res) =>
          setLocationDetailRows(res.data?.marketingMetrics.rows?.map((row) => ({ ...row, ...getRowPatch?.(row) }))),
        )
        .finally(() => setLoading(false))
    }
  }, [])

  return (
    <div className="relative flex h-full max-h-full w-full flex-col overflow-hidden pt-4">
      <SimpleTable data={locationDetailRows} columns={columns} showColumnOrdering={true} />
    </div>
  )
}
